// ── API Envelope ──────────────────────────────────────────────────────────────
export interface ApiResponse<T> {
  data?: T
  error?: string
  message?: string
  meta?: PaginationMeta
}

export interface PaginationMeta {
  page: number
  per_page: number
  total_items: number
  total_pages: number
  has_next: boolean
  has_prev: boolean
}

// ── Auth ──────────────────────────────────────────────────────────────────────

/** Admin/staff user (email + password, role = owner/admin/support/etc.) */
export interface User {
  id: string
  email: string
  first_name: string
  last_name: string
  role: string
  is_verified: boolean
  created_at: string
}

/** Customer (phone + OTP, role = customer) */
export interface Customer {
  id: string
  phone: string
  country_code: string
  country: string
  first_name: string
  last_name: string | null
  email: string
  relationship: string
  marketing_consent: boolean
  phone_verified: boolean
  details_completed: boolean
  is_active: boolean
  last_login_at: string | null
  created_at: string
}

export interface CustomerProfileUpdate {
  first_name: string
  last_name?: string
  email?: string
  relationship?: string
  marketing_consent?: boolean
}

/** Summary row in the customer's report history (GET /customers/me/reports). */
export interface CustomerReportSummary {
  id: string
  scan_id: string
  business_id: string
  business_name: string
  business_logo: string
  overall_score: number
  grade: string
  is_partial: boolean
  created_at: string
}

/** Full persisted report for the customer portal (GET /customers/me/reports/:id). */
export interface CustomerReportDetail {
  id: string
  scan_id: string
  business_id: string
  business_name: string
  overall_score: number
  grade: string
  is_partial: boolean
  created_at: string
  report: ReportPayload
}

export interface TokenPair {
  access_token: string
  refresh_token: string
  expires_in: number
  user_id: string
}

/** Token pair returned by the customer OTP verify endpoint. */
export interface CustomerTokenPair {
  access_token: string
  refresh_token: string
  expires_in: number
  customer_id: string
}

// ── Business ──────────────────────────────────────────────────────────────────
export interface Business {
  id: string
  org_id: string
  name: string
  address: string
  city: string
  state: string
  country: string
  phone: string
  website: string
  /** Canonical (resolved) business category used throughout the system. */
  category: string
  /** Original category reported by Google Places, preserved for transparency. */
  raw_google_category?: string
  latitude: number
  longitude: number
  google_place_id: string
  profile_url: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface BusinessCandidate {
  external_id: string
  source: string
  name: string
  address: string
  city: string
  state: string
  country: string
  phone: string
  website: string
  /** Raw category as reported by Google (may be inaccurate). */
  category: string
  /** Category resolved by our Category Resolution layer; may equal `category`. */
  resolved_category?: string
  /** Confidence in `resolved_category`, in [0,1]. */
  category_confidence?: number
  latitude: number
  longitude: number
  rating: number
  review_count: number
  profile_url: string
  photo_url: string
  is_verified: boolean
}

// ── Scans ─────────────────────────────────────────────────────────────────────
export type ScanStatus =
  | 'created' | 'queued' | 'running'
  | 'partially_completed' | 'completed'
  | 'failed' | 'cancelled' | 'expired'

export type StageStatus = 'pending' | 'running' | 'completed' | 'skipped' | 'failed'

export type ScanStageKey =
  | 'resolve_target_business'
  | 'discover_competitors'
  | 'check_rankings'
  | 'fetch_profile_data'
  | 'fetch_reviews'
  | 'fetch_photos'
  | 'audit_website'
  | 'calculate_scores'
  | 'generate_ai_summary'
  | 'finalize_report'

export interface ScanSession {
  id: string
  org_id: string
  business_id: string
  status: ScanStatus
  trigger_type: string
  report_id: string
  started_at?: string
  completed_at?: string
  created_at: string
  updated_at: string
}

export interface ProgressEvent {
  id: string
  scan_id: string
  stage_key: ScanStageKey
  stage_label: string
  status: StageStatus
  percent: number
  message: string
  scene_payload?: string
  occurred_at: string
}

// ── Scoring ───────────────────────────────────────────────────────────────────
export type CategoryKey = 'search_results' | 'guest_experience' | 'local_listings'

export interface ScoreBreakdown {
  id: string
  category_key: CategoryKey
  sub_category: string
  signal: string
  label: string
  /** Interpolation params for the i18n translation key `report:breakdown.{signal}`. */
  label_params?: Record<string, string | number>
  points: number
  max_points: number
  evidence_ref: string
  severity: 'info' | 'warning' | 'critical'
  is_positive: boolean
}

export interface CategoryScore {
  category: CategoryKey
  label: string
  score: number
  max_score: number
  percentage: number
  grade: string
  breakdowns: ScoreBreakdown[]
}

export interface Scorecard {
  id: string
  scan_id: string
  business_id: string
  policy_version: string
  overall_score: number
  max_score: number
  grade: string
  category_scores: Record<CategoryKey, CategoryScore>
  is_partial: boolean
  confidence_note: string
  created_at: string
}

// ── Competitors ───────────────────────────────────────────────────────────────
export interface CompetitorResult {
  external_id: string
  source: string
  name: string
  address: string
  city: string
  state: string
  latitude: number
  longitude: number
  category: string
  rating: number
  review_count: number
  website: string
  profile_url: string
  distance_meters: number
  has_website: boolean
}

// ── Report ────────────────────────────────────────────────────────────────────
export interface AIRecommendation {
  priority: number
  category: string
  title: string
  description: string
  impact: 'high' | 'medium' | 'low'
  effort: 'high' | 'medium' | 'low'
}

export interface AISummary {
  executive_summary: string
  top_strengths: string[]
  top_weaknesses: string[]
  recommendations: AIRecommendation[]
  competitor_gap: string
  model_used: string
  is_fallback: boolean
}

export interface BusinessProfile {
  external_id: string
  source: string
  name: string
  address: string
  phone: string
  website: string
  category: string
  categories: string[]
  hours: Record<string, string>
  description: string
  rating: number
  review_count: number
  is_verified: boolean
  has_reservations: boolean
  has_ordering: boolean
  has_menu: boolean
  attributes: Record<string, string>
  profile_photo_url: string
  cover_photo_url: string
  /** Google price level 0–4 ($, $$, …); -1 when not provided. */
  price_level: number
  /** Social-media profile URLs derived from the business website. */
  social_links?: string[]
}

export interface ReviewsSnapshot {
  business_external_id: string
  average_rating: number
  total_count: number
  recent_count_30_days: number
  recent_count_90_days: number
  reviews: ReviewItem[]
}

export interface ReviewItem {
  external_id: string
  author_name: string
  rating: number
  text: string
  published_at: string
  owner_reply: string
}

export interface PhotosSnapshot {
  total_count: number
  recent_count_90_days: number
  photos: PhotoItem[]
}

export interface PhotoAIFeedback {
  quality_score: number        // 1–10
  needs_improvement: boolean
  issues: string[]             // e.g. ["blurry", "poor lighting"]
  suggestion: string           // actionable tip for the owner
}

export interface PhotoItem {
  external_id: string
  category: string
  url: string
  thumbnail_url: string
  uploaded_at?: string
  owner_type: string
  ai_feedback?: PhotoAIFeedback | null
}

export interface ReviewAnalysis {
  overall_sentiment: string    // "Very Positive" | "Positive" | "Mixed" | "Needs Attention" | "Critical"
  sentiment_score: number      // 1–10
  positive_themes: string[] | null
  negative_themes: string[] | null
  response_rate_note: string
  suggestions: string[] | null
  reviewed_count: number
}

export interface WebsiteSignals {
  is_reachable: boolean
  status_code: number
  final_url: string
  response_time_ms: number
  title: string
  meta_description: string
  canonical_url: string
  h1: string
  h2s: string[]
  has_mobile_viewport: boolean
  has_local_schema: boolean
  schema_types: string[]
  cta_links: { text: string; href: string; type: string }[]
  has_address_signal: boolean
  has_phone_signal: boolean
  local_keywords: string[]
}

export interface KeywordGridPoint {
  latitude: number
  longitude: number
  position: number    // 1-based; 0 = unranked at this point
  is_ranked: boolean
  /** Marks the grid point at the business's own coordinates (vs. a ring point). */
  is_center: boolean
}

export interface KeywordRanking {
  keyword: string
  position: number    // 1-based; 0 = unranked
  is_ranked: boolean
  top_result: string   // Name of the #1 result for this keyword
  top_result_id: string
  /** True when found within the first page (<=20) of results at the
   * business's own location. is_ranked=true + found_in_top_page=false means
   * found on a deeper page — ranked, but with low visibility. */
  found_in_top_page?: boolean
  /** Mean rank across sampled local geo-grid points where the business
   * appeared (0 when it did not appear at any sampled point). */
  average_position?: number
  /** Fraction (0-1) of geo-grid sample points where the business appeared. */
  coverage?: number
  best_position?: number
  worst_position?: number
  /** Per-location sample results used to render the local-visibility heatmap. */
  grid_points?: KeywordGridPoint[]
}

export interface PlacesSearchResult {
  position: number
  name: string
  place_id: string
  rating?: number
  review_count?: number
  latitude?: number
  longitude?: number
}

export interface GoogleSearchResult {
  domain: string
  title: string
  snippet: string
  link: string
  favicon_url?: string
}

export interface KeywordDetailData {
  keyword: string
  position: number
  is_ranked: boolean
  top_result: string
  maps_results: PlacesSearchResult[]
  search_results: GoogleSearchResult[]
  business_latitude?: number
  business_longitude?: number
  map_image_url?: string
}

export interface SEOIssue {
  severity: 'critical' | 'warning' | 'info' | 'pass'
  category: 'meta' | 'images' | 'links' | 'structure' | 'local' | 'performance'
  title: string
  description: string
  current_value?: string
  recommendation: string
}

export interface SEOAudit {
  score: number       // 0–100
  summary: string
  issues: SEOIssue[]
}

// ── SEO Analysis (deterministic, weighted, structured) ─────────────────────────

export type SEOStatus = 'pass' | 'warning' | 'fail'

export type SEOSectionKey =
  | 'h1'
  | 'title_tag'
  | 'meta_description'
  | 'alt_tags'
  | 'open_graph'
  | 'twitter_card'
  | 'organization_schema'
  | 'local_business_schema'
  | 'item_list_schema'
  | 'product_schema'

export interface SEOCheck {
  key: string
  label: string
  passed: boolean
  /**
   * True when this relevance check could not be reliably verified — e.g. the
   * website content and the business metadata are written in different
   * scripts (Latin vs. Arabic), so a substring/relevance comparison cannot be
   * made. This is a THIRD state, distinct from passed=true/false: it must
   * never render as a red failure or generate an "incomplete" recommendation,
   * but the UI should surface a subtle "not automatically verifiable"
   * indicator rather than silently treating it as passed.
   */
  neutral?: boolean
  /**
   * True for advisory-only checks that are never counted toward a section's
   * pass/fail status and are never scored — e.g. the on-page keyword/name
   * relevance checks (does the H1/title/meta mention the business name,
   * category/niche, or city?). These are genuinely useful recommendations,
   * but too fuzzy/subjective a signal to grade or hard-fail a site over.
   * The UI must render these as neutral "recommended to have" guidance,
   * never as a red failure — see Check.Informational in the backend's
   * seo/analysis.go.
   */
  informational?: boolean
}


export interface SEOSection {
  key: SEOSectionKey
  title: string
  status: SEOStatus
  current_value: string
  checks: SEOCheck[]
  recommendation: string
  how_cartley_helps: string
  score: number
  max_score: number
}

export interface SEOAnalysis {
  score: number          // overall weighted 0–100
  passed_count: number
  warning_count: number
  failed_count: number
  summary: string
  h1: SEOSection
  title_tag: SEOSection
  meta_description: SEOSection
  alt_tags: SEOSection
  open_graph: SEOSection
  twitter_card: SEOSection
  organization_schema: SEOSection
  local_business_schema: SEOSection
  item_list_schema: SEOSection
  product_schema: SEOSection
}

export interface PageSpeedMetric {
  id: 'lcp' | 'fcp' | 'tbt' | 'si' | 'cls'
  numeric_value: number
  display_value: string
  rating: 'good' | 'needs-improvement' | 'poor'
}

export interface PageSpeedResult {
  overall_score: number    // 0–100
  pass: boolean
  load_time_seconds: number
  percent_slower_than: number
  metrics: PageSpeedMetric[]
  report_url: string
  tested_url: string
}

export interface ReportPayload {
  scan_id: string
  business_id: string
  business_name: string
  overall_score: number
  grade: string
  scorecard_result: Scorecard
  competitors: CompetitorResult[]
  profile: BusinessProfile | null
  reviews: ReviewsSnapshot | null
  review_analysis: ReviewAnalysis | null
  photos: PhotosSnapshot | null
  website_signals: WebsiteSignals | null
  seo_audit: SEOAudit | null          // legacy AI audit (older reports)
  seo_analysis: SEOAnalysis | null    // current deterministic, weighted analysis
  page_speed: PageSpeedResult | null
  ai_summary: AISummary | null
  search_rankings: KeywordRanking[] | null
  created_at: string
}

// ── Stage metadata ────────────────────────────────────────────────────────────
export const STAGE_ORDER: ScanStageKey[] = [
  'resolve_target_business',
  'discover_competitors',
  'check_rankings',
  'fetch_profile_data',
  'fetch_reviews',
  'fetch_photos',
  'audit_website',
  'calculate_scores',
  'generate_ai_summary',
  'finalize_report',
]

export const STAGE_LABELS: Record<ScanStageKey, string> = {
  resolve_target_business: 'Locating Business',
  discover_competitors:    'Discovering Competitors',
  check_rankings:          'Checking Search Rankings',
  fetch_profile_data:      'Analyzing Business Profile',
  fetch_reviews:           'Processing Reviews',
  fetch_photos:            'Scanning Media',
  audit_website:           'Auditing Website',
  calculate_scores:        'Calculating Scores',
  generate_ai_summary:     'Generating AI Insights',
  finalize_report:         'Finalizing Report',
}

export const STAGE_ICONS: Record<ScanStageKey, string> = {
  resolve_target_business: '📍',
  discover_competitors:    '🗺️',
  check_rankings:          '🔍',
  fetch_profile_data:      '🏢',
  fetch_reviews:           '⭐',
  fetch_photos:            '📸',
  audit_website:           '🌐',
  calculate_scores:        '📊',
  generate_ai_summary:     '🤖',
  finalize_report:         '✅',
}

export const CATEGORY_LABELS: Record<CategoryKey, string> = {
  search_results:   'Search Results',
  guest_experience: 'Guest Experience',
  local_listings:   'Local Listings',
}

export const CATEGORY_MAX: Record<CategoryKey, number> = {
  search_results:   40,
  guest_experience: 40,
  local_listings:   20,
}

// ── Scan Scene Payload Types ──────────────────────────────────────────────────
// Each type corresponds to a JSON payload emitted by the backend orchestrator
// in the scene_payload field of a ProgressEvent, keyed by "type".

export interface LocateBusinessScene {
  type: 'locate_business'
  lat: number
  lng: number
  name: string
  /** Profile photo URL from Google Places — used to show a logo pin on the map. */
  logo_url?: string
}

export interface CompetitorMapSceneData {
  type: 'competitor_map'
  target_lat: number
  target_lng: number
  competitors: CompetitorResult[]
}

export interface ReviewsSceneData {
  type: 'reviews'
  reviews: { author: string; rating: number; text: string }[]
  total_count: number
  average_rating: number
}

export interface PhotosSceneData {
  type: 'photos'
  photos: { url: string; thumbnail: string; category: string }[]
  total: number
}

export interface WebsiteAuditSceneData {
  type: 'website_audit'
  business_name?: string
  website_url?: string
  logo_url?: string
  category?: string
  phone?: string
  address?: string
  rating?: number
  is_verified?: boolean
  page_title?: string
  meta_description?: string
  has_ssl?: boolean
  response_time_ms?: number
  has_mobile_viewport?: boolean
  has_local_schema?: boolean
  schema_types?: string[]
  h1?: string
  has_address_signal?: boolean
  has_phone_signal?: boolean
}

/** A single category's real score, as calculated by the scoring engine for this business. */
export interface ScoreRevealCategory {
  category: CategoryKey
  score: number
  max_score: number
}

/** Stage 8 payload: the real per-business overall + category scores, emitted
 * when `calculate_scores` completes — used to render the score-reveal animation
 * with actual numbers instead of a placeholder. */
export interface ScoreRevealSceneData {
  type: 'score_reveal'
  overall_score: number
  grade: string
  categories: ScoreRevealCategory[]
}
